import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import '@/App.css';

// Pages
import LandingPage from '@/pages/LandingPage';
import Login from '@/pages/Login';
import Cadastro from '@/pages/Cadastro';
import DashboardSuperAdmin from '@/pages/DashboardSuperAdmin';
import DashboardMasterAdmin from '@/pages/DashboardMasterAdmin';
import DashboardCliente from '@/pages/DashboardCliente';
import DashboardEntregador from '@/pages/DashboardEntregador';
import DashboardFuncionario from '@/pages/DashboardFuncionario';
import FinanceiroCliente from '@/pages/FinanceiroCliente';
import Documentos from '@/pages/Documentos';
import EnderecoEmpresa from '@/pages/EnderecoEmpresa';
import AcessoBloqueado from '@/pages/AcessoBloqueado';
import FolderDivulgacao from '@/pages/FolderDivulgacao';
import AppEntregador from '@/pages/AppEntregador';
import Suporte from '@/pages/Suporte';
import PoliticaPrivacidade from '@/pages/PoliticaPrivacidade';
import ConfiguracoesPlataformaPage from '@/pages/ConfiguracoesPlataformaPage';
import RankingEntregadores from '@/pages/RankingEntregadores';
import TrackingPublico from '@/pages/TrackingPublico';
import GestaoUsuarios from '@/pages/GestaoUsuarios';

// Components
import UpdatePrompt from '@/components/UpdatePrompt';

// Context
import { AuthProvider, useAuth, useAccessControl } from '@/context/AuthContext';
import { RegionProvider } from '@/context/RegionContext';

// Hooks
import useRoutePersistence from '@/hooks/useRoutePersistence';

// Rota privada com verificação de perfil apenas
const PrivateRoute = ({ children, allowedPerfis }) => {
  const { usuario, empresa } = useAuth();
  
  if (!usuario || !empresa) {
    return <Navigate to="/login" />;
  }
  
  // Usa o perfil do usuário, não da empresa
  if (allowedPerfis && !allowedPerfis.includes(usuario.perfil)) {
    return <Navigate to="/login" />;
  }
  
  return children;
};

// Rota protegida com verificação completa de acesso (validação + assinatura)
const ProtectedRoute = ({ children, allowedPerfis }) => {
  const { usuario, empresa } = useAuth();
  const { hasFullAccess } = useAccessControl();
  
  if (!usuario || !empresa) {
    return <Navigate to="/login" />;
  }
  
  if (allowedPerfis && !allowedPerfis.includes(usuario.perfil)) {
    return <Navigate to="/login" />;
  }
  
  // Admins sempre têm acesso total
  const isAdmin = usuario.perfil === 'master_admin' || usuario.perfil === 'super_admin';
  
  // Se não for admin e não tiver acesso completo, redireciona para página de bloqueio
  if (!isAdmin && !hasFullAccess) {
    return <Navigate to="/acesso-bloqueado" />;
  }
  
  return children;
};

function AppContent() {
  useRoutePersistence();
  return (
    <div className="App">
      <UpdatePrompt />
      <Toaster position="top-right" richColors />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/cadastro" element={<Cadastro />} />
        <Route path="/acesso-bloqueado" element={<AcessoBloqueado />} />
        <Route path="/folder" element={<FolderDivulgacao />} />
        <Route path="/suporte" element={<Suporte />} />
        <Route path="/privacidade" element={<PoliticaPrivacidade />} />
        <Route path="/tracking/:token" element={<TrackingPublico />} />
        
        {/* Rotas de Admin - Sem restrição de validação/assinatura */}
        <Route
          path="/dashboard/super-admin"
          element={
            <PrivateRoute allowedPerfis={['super_admin', 'master_admin']}>
              <DashboardSuperAdmin />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/dashboard/master-admin"
          element={
            <PrivateRoute allowedPerfis={['master_admin']}>
              <DashboardMasterAdmin />
            </PrivateRoute>
          }
        />
        
        {/* Configurações da Plataforma (admin) */}
        <Route
          path="/admin/configuracoes"
          element={
            <PrivateRoute allowedPerfis={['master_admin', 'super_admin']}>
              <ConfiguracoesPlataformaPage />
            </PrivateRoute>
          }
        />
        
        {/* Ranking de Entregadores (admin) */}
        <Route
          path="/admin/ranking"
          element={
            <PrivateRoute allowedPerfis={['master_admin', 'super_admin']}>
              <RankingEntregadores />
            </PrivateRoute>
          }
        />
        
        {/* Gestão de Usuários (master_admin) */}
        <Route
          path="/admin/usuarios"
          element={
            <PrivateRoute allowedPerfis={['master_admin']}>
              <GestaoUsuarios />
            </PrivateRoute>
          }
        />
        
        {/* Dashboard Cliente - Requer validação + assinatura */}
        <Route
          path="/dashboard/cliente"
          element={
            <ProtectedRoute allowedPerfis={['cliente']}>
              <DashboardCliente />
            </ProtectedRoute>
          }
        />
        
        {/* Páginas de configuração do Cliente - Sem restrição total (permite completar cadastro) */}
        <Route
          path="/dashboard/cliente/financeiro"
          element={
            <PrivateRoute allowedPerfis={['cliente']}>
              <FinanceiroCliente />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/dashboard/cliente/documentos"
          element={
            <PrivateRoute allowedPerfis={['cliente']}>
              <Documentos />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/dashboard/cliente/endereco"
          element={
            <PrivateRoute allowedPerfis={['cliente']}>
              <EnderecoEmpresa />
            </PrivateRoute>
          }
        />
        
        {/* Dashboard Entregador - Requer validação + assinatura */}
        <Route
          path="/dashboard/entregador"
          element={
            <ProtectedRoute allowedPerfis={['entregador']}>
              <DashboardEntregador />
            </ProtectedRoute>
          }
        />
        
        {/* App Mobile Entregador */}
        <Route
          path="/app/entregador"
          element={
            <ProtectedRoute allowedPerfis={['entregador']}>
              <AppEntregador />
            </ProtectedRoute>
          }
        />
        
        {/* Páginas de configuração do Entregador - Sem restrição total */}
        <Route
          path="/dashboard/entregador/endereco"
          element={
            <PrivateRoute allowedPerfis={['entregador']}>
              <EnderecoEmpresa />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/dashboard/entregador/documentos"
          element={
            <PrivateRoute allowedPerfis={['entregador']}>
              <Documentos />
            </PrivateRoute>
          }
        />
        
        <Route
          path="/dashboard/entregador/financeiro"
          element={
            <PrivateRoute allowedPerfis={['entregador']}>
              <FinanceiroCliente />
            </PrivateRoute>
          }
        />
        
        {/* Dashboard Funcionário - Requer validação + assinatura da empresa do entregador */}
        <Route
          path="/dashboard/funcionario"
          element={
            <ProtectedRoute allowedPerfis={['funcionario']}>
              <DashboardFuncionario />
            </ProtectedRoute>
          }
        />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <RegionProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </RegionProvider>
    </BrowserRouter>
  );
}

export default App;
